/**
 * Genererar en 8760-timmars syntetisk timserie från wizardinmatning.
 *
 * Output speglar HourPoint-formatet som Tibber-flödet ger, så resten
 * av appen (simulering, grafer, paneler) fungerar oförändrat.
 *
 * BEGRÄNSNINGAR — viktigt att kommunicera till användaren:
 * - Typkurvorna är handgjorda approximationer, inte aggregerad data.
 *   Plan: när vi har 50+ verkliga profiler per kategori i analytics-
 *   banken blandar vi in dem (steg 2 av profilbanks-arbetet).
 * - Wizard-användare har dataSource='wizard' i analytics, vilket gör att
 *   deras data ALDRIG flödar tillbaka i typprofilerna. Att aggregera
 *   wizardgissningar skulle skapa självreferentiella profiler — garbage
 *   in, garbage out.
 * - Vi modellerar inte dag/helg-skillnad, semester-mönster eller individu-
 *   ella vanor. Allt blir en jämn mittenversion. För batteribesparing är
 *   detta tillräckligt, eftersom arbitrage på spotpriser är samma
 *   matematik oavsett vem som drar elen.
 */

import type { HourPoint } from "../sim/types";

// Värmekällor — alla med olika säsongsmönster.
export type HeatingType =
  | "apartment" // lägenhet (ofta jämn, fjärrvärme i kallhyra)
  | "district_heating" // villa med fjärrvärme (bara hushållsel + ev. tappvarmvatten-el)
  | "heat_pump_ground" // bergvärme (mjuk säsongskurva, hög effektivitet)
  | "heat_pump_air" // luftvärmepump (brantare kurva, sämre vid sträng kyla)
  | "direct_electric"; // direktverkande el (extrem vintertopp)

export interface WizardInput {
  priceArea: "SE1" | "SE2" | "SE3" | "SE4";
  /** Total elförbrukning per år (kWh) inklusive ev. uppvärmning */
  annualKWh: number;
  heatingType: HeatingType;
  hasSolar: boolean;
  /** Solens årsproduktion i kWh (0 om hasSolar=false) */
  solarAnnualKWh: number;
  hasEv: boolean;
  /** EV-körning per år, km (0 om hasEv=false). Vi använder 0.18 kWh/km. */
  evKmPerYear: number;
  /**
   * Frivilligt två-siffrigt postnummer-prefix (länsnivå). Skickas till
   * analytics som geo-signal för framtida sol-modul, men påverkar inte
   * generatorns matematik nu (sol-instrålning är fortfarande samma kurva
   * över hela Sverige tills vi byter till PVGIS-data).
   */
  postalCodePrefix?: string;
}

/**
 * Säsongsamplitud — hur mycket vinter-toppen är högre än sommar-bottnen.
 * 0 = helt jämn, 1 = stor swing. Värdena är grova approximationer av
 * svenska typhushåll. När vi har egen aggregerad data byter vi.
 */
const HEATING_AMPLITUDE: Record<HeatingType, number> = {
  apartment: 0.12,
  district_heating: 0.18,
  heat_pump_ground: 0.4,
  heat_pump_air: 0.48,
  direct_electric: 0.62,
};

/**
 * Genererar månadsfördelning som andelar (summa = 1.0) med kosinus-modell:
 * jan = peak, jul = botten. Större amplitud = brantare kurva.
 */
function generateMonthlyShape(amplitude: number): number[] {
  const raw = Array.from({ length: 12 }, (_, i) => {
    // i=0 (jan) ger cos(0) = 1, i=6 (jul) ger cos(π) = -1
    const cosValue = Math.cos((i * 2 * Math.PI) / 12);
    return 1 + amplitude * cosValue;
  });
  const sum = raw.reduce((s, v) => s + v, 0);
  return raw.map((v) => v / sum);
}

/**
 * Dygnsmönster för hushållsel. Morgon- och kvällstopp, lågt nattetid.
 * Handgjord från typsvenskt mönster. Summan = 1.0 (per dygn).
 * Samma form används för alla värmekällor — säsongsskalningen sker
 * via månadsfördelningen ovan.
 */
const DAILY_SHAPE_HOUSEHOLD = [
  0.025, 0.02, 0.018, 0.018, 0.022, 0.032, 0.045, 0.06, // 00-07
  0.055, 0.045, 0.04, 0.04, 0.045, 0.045, 0.045, 0.05, // 08-15
  0.055, 0.06, 0.065, 0.06, 0.05, 0.04, 0.035, 0.03, // 16-23
];

/**
 * Sol-andel per månad. Approximation av svensk solinstrålning:
 * vinter 1-3%, sommar 14-15%. Summa ≈ 1.0.
 * SE1/SE2 har något lägre vinterproduktion men vi använder samma kurva
 * eftersom relationen mellan månaderna är liknande.
 */
const SOLAR_MONTHLY_SHARE = [
  0.012, 0.032, 0.072, 0.105, 0.135, 0.15, 0.142, 0.122, 0.097, 0.07, 0.03, 0.013,
];

/**
 * Sol-produktion per timme. Gaussisk kurva centrerad på 12:00 med
 * sigma = 3 timmar. Inga timmar producerar mellan 21 och 04.
 */
function dailySolarShape(): number[] {
  const shape = Array.from({ length: 24 }, (_, h) => {
    const distFromNoon = Math.abs(h - 12);
    return Math.exp(-(distFromNoon * distFromNoon) / (2 * 3 * 3));
  });
  // Nolla nattetimmar
  for (let h = 0; h < 5; h++) shape[h] = 0;
  for (let h = 22; h < 24; h++) shape[h] = 0;
  const sum = shape.reduce((s, v) => s + v, 0);
  return shape.map((v) => v / sum);
}

const SOLAR_DAILY_SHAPE = dailySolarShape();

/**
 * EV-laddning fördelad över natten (smart laddning är default-antagandet).
 * Lika fördelning över 23-05 — det är när folk laddar med Tibber Charging
 * eller liknande. Summa över dygn = 1.0.
 */
function dailyEvShape(): number[] {
  const shape = Array.from({ length: 24 }, () => 0);
  // 23, 00, 01, 02, 03, 04, 05 = 7 timmar
  const chargingHours = [23, 0, 1, 2, 3, 4, 5];
  for (const h of chargingHours) shape[h] = 1 / chargingHours.length;
  return shape;
}

const EV_DAILY_SHAPE = dailyEvShape();

/**
 * Hjälp: hur många dagar i månaden m (0=jan) under år year.
 */
function daysInMonth(year: number, m: number): number {
  return new Date(year, m + 1, 0).getDate();
}

/**
 * Bygger ISO timestamp för en lokal timme i Stockholm. Approximerar DST
 * via månadsbaserad offset (apr-okt = +02, nov-mar = +01). Inte exakt
 * vid själva övergångshelgen men felet är <0.5% över året.
 */
function isoForLocalHour(year: number, month: number, day: number, hour: number): string {
  const isDst = month >= 3 && month <= 9; // apr-okt (0-indexerat: 3..9)
  const offset = isDst ? "+02:00" : "+01:00";
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  const hh = String(hour).padStart(2, "0");
  return `${year}-${mm}-${dd}T${hh}:00:00${offset}`;
}

export interface GenerateResult {
  hours: HourPoint[];
  /** Diagnostik som UI kan visa för transparens */
  diagnostics: {
    actualAnnualImportKWh: number;
    actualAnnualExportKWh: number;
    /** Vintertopp (dec+jan+feb) som andel av året */
    winterImportShare: number;
  };
}

/**
 * Genererar 8760 timmar (eller 8784 vid skottår, vi använder normalår).
 *
 * Algoritm:
 *   1. Beräkna brutto-konsumtion per timme = årlig × månads-shape × dygns-shape
 *   2. Lägg på EV-laddning per timme (om hasEv) — separat från shape:n
 *      eftersom EV laddar koncentrerat nattetid
 *   3. Beräkna solproduktion per timme (om hasSolar)
 *   4. Netto: import = max(0, konsumtion - sol), export = max(0, sol - konsumtion)
 *
 * Vi modellerar bruttokonsumtion (allt hushållet drar) och låter
 * solen kvitta i samma timme. Det är vad batteri-simuleringen sedan
 * arbetar mot.
 */
export function generateHoursFromWizard(input: WizardInput): GenerateResult {
  // Använd senaste 12 hela månader rullande från idag. Garanterar att
  // spotpriserna finns i upstream (elprisetjustnu.se täcker historiska
  // data några år bakåt, men cachen är säkrast nära nutid). Tibber-flödet
  // gör samma sak. Resultat: maj 2025 - april 2026 om dagens är maj 2026.
  const today = new Date();
  const startMonth = today.getMonth(); // 0-11, samma månad ett år bakåt
  const startYear = today.getFullYear() - 1;

  const householdAnnual = Math.max(0, input.annualKWh);
  const monthlyShape = generateMonthlyShape(
    HEATING_AMPLITUDE[input.heatingType] ?? 0.3,
  );

  // EV-bidrag baseras på årlig körsträcka × 0.18 kWh/km (svensk genomsnittlig
  // EV-förbrukning inkl laddförluster). Subtraheras från householdAnnual så
  // monthly-shape:n bara appliceras på hushållets icke-EV-del.
  const evAnnualKWh = input.hasEv ? Math.max(0, input.evKmPerYear) * 0.18 : 0;
  const baseHouseholdAnnual = Math.max(0, householdAnnual - evAnnualKWh);

  const solarAnnual = input.hasSolar ? Math.max(0, input.solarAnnualKWh) : 0;

  const hours: HourPoint[] = [];
  let actualImport = 0;
  let actualExport = 0;
  let winterImport = 0;

  // Loop över 12 månader rullande
  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonth + i) % 12; // 0-11, säsongsbaserad shape
    const calendarMonth = (startMonth + i) % 12;
    const calendarYear = startYear + Math.floor((startMonth + i) / 12);
    const days = daysInMonth(calendarYear, calendarMonth);
    const monthlyConsumption =
      baseHouseholdAnnual * (monthlyShape[monthIndex] ?? 1 / 12);
    const dailyConsumption = monthlyConsumption / days;
    const monthlySolar = solarAnnual * (SOLAR_MONTHLY_SHARE[monthIndex] ?? 0);
    const dailySolar = monthlySolar / days;
    const dailyEv = evAnnualKWh / 365;

    for (let d = 1; d <= days; d++) {
      for (let h = 0; h < 24; h++) {
        const consumption =
          dailyConsumption * (DAILY_SHAPE_HOUSEHOLD[h] ?? 0) +
          dailyEv * (EV_DAILY_SHAPE[h] ?? 0);
        const solar = dailySolar * (SOLAR_DAILY_SHAPE[h] ?? 0);

        const netDraw = consumption - solar;
        const importKWh = Math.max(0, netDraw);
        const exportKWh = Math.max(0, -netDraw);

        hours.push({
          from: isoForLocalHour(calendarYear, calendarMonth, d, h),
          importKWh: Math.round(importKWh * 1000) / 1000,
          exportKWh: Math.round(exportKWh * 1000) / 1000,
          spotPrice: 0,
        });

        actualImport += importKWh;
        actualExport += exportKWh;
        // Vintern definierad som dec/jan/feb oavsett vilket år de tillhör
        if (calendarMonth === 11 || calendarMonth === 0 || calendarMonth === 1) {
          winterImport += importKWh;
        }
      }
    }
  }

  return {
    hours,
    diagnostics: {
      actualAnnualImportKWh: Math.round(actualImport),
      actualAnnualExportKWh: Math.round(actualExport),
      winterImportShare: actualImport > 0 ? winterImport / actualImport : 0,
    },
  };
}
